import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/shared/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import { getSeasonalContext } from '../utils/seasonalCalendar';
import useLimits from '../utils/useLimits';
import usePricing from '../utils/usePricing';
import TopUpModal from '../components/TopUpModal';
import { trackTopUpOpened, trackDashboardGenerateCta } from '../utils/analytics';
import ProductTour, { useTour } from '../components/ProductTour';

// ── All dashboard components ────────────────────────────────────────────────
import LunaMascot from '../components/LunaMascot';
import StoryRecapCard from '../components/StoryRecapCard';
import ProfileCompletionBar from '../components/ProfileCompletionBar';
import ThemeVotingCard from '../components/ThemeVotingCard';
import WishlistWidget from '../components/WishlistWidget';
import InsightsCard from '../components/InsightsCard';
import StreakCard from '../components/StreakCard';
import AchievementsCard from '../components/AchievementsCard';
import ChildIdentityBadge from '../components/ChildIdentityBadge';
import ReadingPassportWidget from '../components/ReadingPassportWidget';
import RecommendationsCard from '../components/RecommendationsCard';
import TomorrowPreviewCard from '../components/TomorrowPreviewCard';
import ReminderSuggestionCard from '../components/ReminderSuggestionCard';
import ParentLetterCard from '../components/ParentLetterCard';
import GrowthDashboard from '../components/GrowthDashboard';
import ReferralCard from '../components/ReferralCard';
import { isFamilyEntitled, canGenerateStories, entitledPlan } from '../utils/entitlements';

const PACK_LABELS = { story: 'Story Pack', narration: 'Narration Pack', voice: 'Voice Pack' };

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentStories, setRecentStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState([]);

  // Surprise Me state
  const [surpriseChildId, setSurpriseChildId] = useState(null);
  const [surpriseLoading, setSurpriseLoading] = useState(false);
  const [surpriseError, setSurpriseError] = useState('');
  const [tonight, setTonight] = useState(null);
  const [topUpToast, setTopUpToast] = useState(null);
  const [showTopUp, setShowTopUp] = useState(false);
  const [topUpType, setTopUpType] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { usage, limits, topUpCredits, loading: limitsLoading, refresh: refreshLimits } = useLimits();
  const seasonal = getSeasonalContext();

  // ThemeVotingCard: dismissed per-session via state (shows once, user picks or skips)
  const [themeCardDismissed, setThemeCardDismissed] = useState(false);

  useEffect(() => {
    API.get('/users/stats').then(res => {
      setStats(res.data.stats);
      setRecentStories(res.data.recentStories);
    }).catch(console.error).finally(() => setLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => {
    API.get('/children').then(res => {
      const kids = res.data.children || [];
      setChildren(kids);
      if (kids.length > 0) setSurpriseChildId(kids[0]._id);
    }).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!surpriseChildId) return undefined;
    setTonight(null);
    // Same race as the widgets below: switching children quickly could let an
    // earlier response land last and show the wrong child's suggestion.
    let cancelled = false;
    API.get(`/stories/tonight/${surpriseChildId}`)
      .then(res => { if (!cancelled) setTonight(res.data.tonight); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [surpriseChildId]);

  useEffect(() => {
    const topup = searchParams.get('topup');
    const pack  = searchParams.get('pack');
    if (topup === 'success' && pack) {
      setTopUpToast(PACK_LABELS[pack] || 'Top-up Pack');
      const p = new URLSearchParams(searchParams);
      p.delete('topup'); p.delete('pack');
      setSearchParams(p, { replace: true });
      setTimeout(() => setTopUpToast(null), 5000);
    }
  }, []); // eslint-disable-line

  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const { showTour, doneTour } = useTour();

  // The plan in force, resolved as the server resolves it — a lapsed
  // subscription, an expired gift and a co-parent's family plan included.
  // Read raw, a lapsed Family subscriber got a "Family Plan" badge, and a
  // co-parent on a Family plan a "Free Plan" badge, an Upgrade button and no
  // narration or regen counters.
  const plan        = entitledPlan(user);
  const isPaid      = plan !== 'free';
  const { pricing } = usePricing();
  const isFamily    = isFamilyEntitled(user);   // includes co-parents on a Family plan
  const canGenerate = canGenerateStories(user); // false for read_only co-parents
  // Two bugs lived on this line. It read the raw `plan`, so a lapsed Family
  // subscriber — whose plan stays 'family' after Stripe cancels, with only
  // `status` changing — was shown "x / 120 stories" while the backend enforced
  // 5 via effectivePlan(). And it hardcoded the tiers, so the admin panel's
  // configurable plan limits never reached this display at all.
  //
  // `limits` from useLimits() is the server's own number (already computed with
  // effectivePlan and the admin overrides), and it is destructured above. Keep a
  // synchronous fallback for the first paint so the figure does not visibly jump
  // while that request is in flight (`plan` is already the entitled one).
  const fallbackStoriesLimit = plan === 'family' ? 120 : plan === 'basic' ? 60 : 5;
  const planStoriesLimit = (!limitsLoading && Number.isFinite(limits?.stories))
    ? limits.stories
    : fallbackStoriesLimit;
  const used        = user?.storiesGeneratedThisMonth || 0;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = user?.name?.split(' ')[0] || 'there';

  const planMeta = {
    free:   { label: 'Free',   color: '#6c757d', bg: '#f8f9fa' },
    basic:  { label: 'Basic',  color: '#6f42c1', bg: '#f0e8ff' },
    family: { label: 'Family', color: '#fd7e14', bg: '#fff3e0' },
  };
  const pm = planMeta[plan] || planMeta.free;

  // The active child for per-child widgets (tracks sidebar selection)
  const activeChild = children.find(c => c._id === surpriseChildId) || children[0] || null;

  // ── Surprise Me: one-tap generation ────────────────────────────────────────
  const handleSurpriseMe = async () => {
    const child = children.find(c => c._id === surpriseChildId);
    if (!child) return;
    setSurpriseLoading(true);
    setSurpriseError('');
    try {
      let body;
      if (tonight && tonight.childId === child._id) {
        body = tonight.params;
      } else {
        const tone      = child.storyPreferences?.tone || 'magical';
        const interests = child.interests?.length ? child.interests : [];
        const pool      = child.lessonThemes?.filter(Boolean) || [];
        const themes    = pool.length ? [pool[Math.floor(Math.random() * pool.length)]] : [];
        body = { childId: child._id, interests, themes, tone, language: 'en' };
      }
      const res = await API.post('/stories/generate', body);
      trackDashboardGenerateCta();
      refreshLimits();
      if (res.data.pendingApproval) {
        navigate('/dashboard/approval-queue?new=true');
      } else {
        navigate(`/dashboard/story/${res.data.storyId}?new=true&generating=true`);
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Story generation failed. Please try again.';
      setSurpriseError(msg);
      if (err.response?.data?.limitReached) setShowTopUp(true);
    } finally {
      setSurpriseLoading(false);
    }
  };

  // ── ThemeVotingCard: navigate to generate with chosen theme ────────────────
  const handleThemeSelected = (themeName) => {
    setThemeCardDismissed(true);
    const params = new URLSearchParams();
    if (surpriseChildId) params.set('childId', surpriseChildId);
    params.set('theme', themeName);
    navigate(`/dashboard/generate?${params.toString()}`);
  };

  return (
    <DashboardLayout
      sidebarOpen={mobileSidebarOpen}
      onSidebarChange={setMobileSidebarOpen}
      tourActive={showTour}
    >
      {/* ── Top-up success toast ─────────────────────────────────────────── */}
      {topUpToast && (
        <div style={{
          position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999,
          background: 'linear-gradient(135deg,#16a34a,#15803d)',
          color: 'white', borderRadius: '14px', padding: '14px 20px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          display: 'flex', alignItems: 'center', gap: '10px',
          animation: 'fadeIn 0.3s ease',
        }}>
          <span style={{ fontSize: '22px' }}>⚡</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: '14px' }}>{topUpToast} activated!</div>
            <div style={{ fontSize: '12px', opacity: 0.85 }}>Credits added to your account.</div>
          </div>
          <button onClick={() => setTopUpToast(null)}
            className="btn-close btn-close-white ms-2" style={{ fontSize: '10px' }} />
        </div>
      )}

      <div style={{ maxWidth: '1040px' }}>

        {/* ══════════════════════════════════════════════════════════════════
            ZONE 1 — GREETING
            Emotional hook via Luna mascot (time-aware, streak-aware).
            Peak-End rule: strong opening = remembered positively.
        ══════════════════════════════════════════════════════════════════ */}
        <div className="mb-3">
          <LunaMascot user={user} />
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            ZONE 2 — PRIMARY ACTION (above the fold)
            Von Restorff: the generate button is the standout element.
            Fogg Behavior Model: high motivation + one clear trigger.
        ══════════════════════════════════════════════════════════════════ */}
        <div className="rounded-4 p-4 mb-4 text-white"
          style={{ background: 'linear-gradient(135deg,#2d1b69,#6f42c1)', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position:'absolute', top:'-20px', right:'-20px', width:'120px', height:'120px', borderRadius:'50%', background:'rgba(255,255,255,0.06)' }} />
          <div style={{ position:'absolute', bottom:'-30px', right:'80px', width:'80px', height:'80px', borderRadius:'50%', background:'rgba(255,255,255,0.06)' }} />

          <div style={{ position:'relative' }}>
            <h3 className="fw-bold mb-1" style={{ fontFamily:"'Fredoka One',cursive", fontSize:'clamp(20px,4vw,28px)' }}>
              {greeting}, {firstName}! 🌙
            </h3>

            {children.length === 0 ? (
              <>
                <p className="mb-3" style={{ color:'#c9a8ff', fontSize:'15px' }}>
                  Ready to create tonight's magical story?
                </p>
                <Link to="/dashboard/generate" data-tour="generate-btn"
                  className="btn btn-warning fw-bold rounded-pill px-4"
                  onClick={trackDashboardGenerateCta}>
                  <i className="bi bi-magic me-2"></i>Generate Story
                </Link>
              </>
            ) : (
              <>
                <p className="mb-3" style={{ color:'#c9a8ff', fontSize:'15px' }}>
                  Ready to create tonight's magical story?
                </p>

                {children.length > 1 && (
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    {children.map(c => (
                      <button key={c._id}
                        onClick={() => setSurpriseChildId(c._id)}
                        className="btn btn-sm rounded-pill fw-semibold"
                        style={{
                          background: surpriseChildId === c._id ? 'rgba(255,193,7,0.25)' : 'rgba(255,255,255,0.1)',
                          border: surpriseChildId === c._id ? '2px solid #ffc107' : '1px solid rgba(255,255,255,0.3)',
                          color: '#fff', padding: '4px 14px', transition: 'all 0.15s',
                        }}>
                        {c.avatar || '🧒'} {c.name}
                      </button>
                    ))}
                  </div>
                )}

                {tonight && tonight.childId === surpriseChildId ? (
                  <div className="mb-3 p-3 rounded-3" style={{ background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.18)' }}>
                    <div style={{ color:'#f9c74f', fontSize:'12px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'4px' }}>
                      ✨ Tonight's story idea
                    </div>
                    <div style={{ color:'#fff', fontSize:'14px', lineHeight:1.6 }}>
                      {tonight.conceptLine}
                    </div>
                    {tonight.basis?.length > 0 && (
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {tonight.basis.map(b => (
                          <span key={b.type} className="badge rounded-pill" style={{ background:'rgba(255,255,255,0.12)', color:'#c9a8ff', fontSize:'11px', fontWeight:500, padding:'4px 10px' }}>
                            {b.label}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (() => {
                  const sc = children.find(c => c._id === surpriseChildId);
                  if (!sc) return null;
                  const parts = [];
                  if (sc.interests?.length) parts.push(sc.interests.slice(0, 3).join(', '));
                  if (sc.storyPreferences?.tone) parts.push(`${sc.storyPreferences.tone} tone`);
                  return (
                    <p className="mb-3" style={{ color:'rgba(255,255,255,0.6)', fontSize:'13px' }}>
                      {children.length === 1 ? 'For ' : ''}{sc.name}
                      {parts.length > 0 ? ` · ${parts.join(' · ')}` : ''}
                    </p>
                  );
                })()}

                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <button
                    data-tour="generate-btn"
                    className="btn btn-warning fw-bold rounded-pill px-4 py-2"
                    style={{ fontSize:'15px', minWidth:'220px' }}
                    onClick={handleSurpriseMe}
                    disabled={surpriseLoading || !surpriseChildId || !canGenerate}
                  >
                    {surpriseLoading
                      ? <><span className="spinner-border spinner-border-sm me-2" style={{ width:'14px', height:'14px', borderWidth:'2px' }} />Crafting your story…</>
                      : <><i className="bi bi-stars me-2"></i>Generate Tonight's Story</>}
                  </button>
                  <Link to="/dashboard/generate"
                    className="btn rounded-pill px-3"
                    style={{ color:'rgba(255,255,255,0.85)', border:'1px solid rgba(255,255,255,0.35)', background:'transparent', fontSize:'14px' }}
                    onClick={trackDashboardGenerateCta}>
                    <i className="bi bi-sliders me-1"></i>Customise
                  </Link>
                </div>

                {surpriseError && (
                  <div className="mt-3 px-3 py-2 rounded-3 small"
                    style={{ background:'rgba(220,53,69,0.22)', border:'1px solid rgba(220,53,69,0.45)', color:'#fff' }}>
                    {surpriseError}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            ZONE 3 — CONTEXTUAL RECALL
            StoryRecapCard renders itself only when returning after 3+ days.
            Zeigarnik: "you left this unfinished" = powerful re-engagement.
        ══════════════════════════════════════════════════════════════════ */}
        <StoryRecapCard />

        {/* ══════════════════════════════════════════════════════════════════
            ZONE 4 — ONBOARDING PROGRESS
            Zeigarnik + Goal-gradient: near-complete tasks compel action.
            Hidden at 100% so it disappears once the user is set up.
        ══════════════════════════════════════════════════════════════════ */}
        <ProfileCompletionBar user={user} children={children} storiesCount={stats?.totalStories || 0} />

        {/* ══════════════════════════════════════════════════════════════════
            ZONE 5 — SEASONAL MOMENT
            Temporal scarcity: holidays feel urgent.
            Only renders when a holiday is active or within ~7 days.
        ══════════════════════════════════════════════════════════════════ */}
        {(seasonal.active || seasonal.upcoming) && (() => {
          const h = seasonal.active || seasonal.upcoming?.holiday;
          const daysAway = seasonal.upcoming?.daysAway;
          const params = new URLSearchParams({
            seasonalId: h.id,
            themes: h.themes.join(','),
            tone: h.tone,
            prompt: h.suggestedPrompt,
          });
          return (
            <div className="rounded-4 p-3 mb-4 d-flex align-items-center justify-content-between flex-wrap gap-3"
              style={{ background: h.gradient, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: '-15px', right: '-15px', width: '90px', height: '90px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
              <div className="d-flex align-items-center gap-3">
                <span style={{ fontSize: '36px', lineHeight: 1 }}>{h.emoji}</span>
                <div>
                  <div className="fw-bold text-white" style={{ fontSize: '15px', fontFamily: "'Fredoka One',cursive" }}>
                    {seasonal.active ? `It's ${h.name}!` : `${h.name} in ${daysAway} day${daysAway === 1 ? '' : 's'}!`}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px' }}>{h.description}</div>
                </div>
              </div>
              <Link to={`/dashboard/generate?${params}`}
                className="btn btn-sm fw-semibold rounded-pill px-4 flex-shrink-0"
                style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.4)', backdropFilter: 'blur(4px)' }}>
                <i className="bi bi-magic me-1"></i>Generate {h.name} Story
              </Link>
            </div>
          );
        })()}

        {/* ══════════════════════════════════════════════════════════════════
            ZONE 6 — QUICK AT-A-GLANCE STATS
            Miller's Law: 3 numbers, instantly scannable.
            Compact pills = low visual weight, high information density.
        ══════════════════════════════════════════════════════════════════ */}
        <div className="d-flex flex-wrap gap-2 mb-4">
          {[
            { label: 'Stories this month', value: loading ? '…' : `${used} / ${planStoriesLimit}`, icon: 'bi-calendar-heart', color: '#6f42c1', to: '/dashboard/library' },
            { label: 'Total stories',      value: loading ? '…' : (stats?.totalStories ?? 0),       icon: 'bi-book-fill',      color: '#0d6efd', to: '/dashboard/library' },
            { label: 'Favourites',         value: loading ? '…' : (stats?.favoriteStories ?? 0),    icon: 'bi-heart-fill',     color: '#dc3545', to: '/dashboard/library' },
          ].map(s => (
            <Link key={s.label} to={s.to} className="text-decoration-none">
              <div className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill"
                style={{ background: 'var(--card-bg)', border: '1px solid var(--border-purple)', fontSize: '13px' }}>
                <i className={`bi ${s.icon}`} style={{ color: s.color }}></i>
                <span className="fw-bold" style={{ color: 'var(--text-main)' }}>{s.value}</span>
                <span style={{ color: 'var(--text-soft)' }}>{s.label}</span>
              </div>
            </Link>
          ))}
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            ZONE 7 — MAIN CONTENT + ENGAGEMENT SIDEBAR
            Two-column: left = story feed & decision helpers
                        right = habit/progress widgets (progressive disclosure)
        ══════════════════════════════════════════════════════════════════ */}
        <div className="row g-4">

          {/* ── LEFT COLUMN: Decision helpers + Activity feed ──────────── */}
          <div className="col-lg-7 d-flex flex-column gap-4">

            {/* Theme picker — helps decide tonight's story direction.
                Dismissed per-session; navigates to generate with theme preset. */}
            {children.length > 0 && !themeCardDismissed && (
              <ThemeVotingCard
                childName={activeChild?.name}
                onThemeSelected={handleThemeSelected}
                onSkip={() => setThemeCardDismissed(true)}
              />
            )}

            {/* Wishlist — children's saved story wishes */}
            <WishlistWidget />

            {/* Family insights — weekly digest (self-hides when no data) */}
            <InsightsCard />

            {/* Recent stories — activity feed */}
            <div className="card border-0 shadow-sm" style={{ borderRadius:'18px', overflow:'hidden' }}>
              <div className="d-flex align-items-center justify-content-between px-4 py-3 border-bottom" style={{ background:'var(--header-bg)' }}>
                <h6 className="fw-bold mb-0" style={{ color:'var(--text-main)' }}>
                  <i className="bi bi-clock-history me-2" style={{ color:'#6f42c1' }}></i>Recent Stories
                </h6>
                <Link to="/dashboard/library" className="btn btn-sm btn-outline-secondary rounded-pill px-3" style={{ fontSize:'12px' }}>
                  View All
                </Link>
              </div>
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border" style={{ color:'#6f42c1' }}></div>
                </div>
              ) : recentStories.length === 0 ? (
                <div className="text-center py-5 px-4">
                  <div style={{ fontSize:'48px', marginBottom:'12px' }}>📖</div>
                  <h6 className="fw-bold mb-2" style={{ color:'var(--text-main)' }}>No stories yet</h6>
                  <p className="text-muted small mb-3">Generate your first personalised bedtime story in seconds.</p>
                  <Link to="/dashboard/generate" className="btn btn-dream rounded-pill px-4">
                    <i className="bi bi-magic me-2"></i>Generate your first story
                  </Link>
                </div>
              ) : (
                <div>
                  {recentStories.map((s, i) => (
                    <Link to={`/dashboard/story/${s._id}`} key={s._id}
                      className="d-flex align-items-center gap-3 px-4 py-3 text-decoration-none"
                      style={{ borderBottom: i < recentStories.length - 1 ? `1px solid var(--row-sep)` : 'none', transition:'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--item-bg)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <div className="d-flex align-items-center justify-content-center rounded-3"
                        style={{ width:'44px', height:'44px', background:'var(--purple-section)', fontSize:'22px', flexShrink:0 }}>
                        {s.child?.avatar || '🧒'}
                      </div>
                      <div className="flex-grow-1 overflow-hidden">
                        <div className="fw-semibold text-truncate" style={{ color:'var(--text-main)', fontSize:'14px' }}>{s.title}</div>
                        <div style={{ color:'var(--text-soft)', fontSize:'12px', marginTop:'2px' }}>
                          {s.child?.name} · {new Date(s.createdAt).toLocaleDateString()}
                          {s.language && s.language !== 'en' && (
                            <span className="ms-2 badge rounded-pill" style={{ background:'var(--badge-yellow-bg)', color:'var(--badge-yellow-fg)', fontSize:'10px' }}>
                              {s.language.toUpperCase()}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        {s.isFavorite && <i className="bi bi-heart-fill" style={{ color:'#dc3545', fontSize:'13px' }}></i>}
                        {s.illustrationUrl && <i className="bi bi-image" style={{ color:'#6f42c1', fontSize:'13px' }}></i>}
                        <i className="bi bi-chevron-right" style={{ color:'#ccc', fontSize:'13px' }}></i>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

          </div>

          {/* ── RIGHT COLUMN: Habit + Progress widgets ─────────────────── */}
          <div className="col-lg-5 d-flex flex-column gap-3">

            {/* 1. Streak — habit loop reinforcement (Duhigg: cue → routine → reward) */}
            <StreakCard />

            {/* 2. Achievements — variable reward schedule (Skinner box effect) */}
            <AchievementsCard />

            {/* 3. Child identity badge — sense of belonging, personalisation */}
            {activeChild && (
              <ChildIdentityBadge
                childName={activeChild.name}
                favoriteGenre={activeChild.interests?.[0]}
                compact={true}
              />
            )}

            {/* 4. Reading Passport — 30-day activity grid (visual momentum) */}
            {/* key: these widgets fetch on childId and none of them guards the
                response. Switching children faster than a round-trip left the
                previous child's data on screen — and this one takes childName as
                a prop, so it could render "Emma's Reading Passport" above Noah's
                grid. Remounting discards the stale instance. */}
            {activeChild && (
              <ReadingPassportWidget
                key={activeChild._id}
                childId={activeChild._id}
                childName={activeChild.name}
              />
            )}

            {/* 5. Recommendations — AI personalization (relevance = engagement) */}
            <RecommendationsCard key={surpriseChildId} childId={surpriseChildId} />

            {/* 6. Tomorrow preview — appointment making, anticipation */}
            {surpriseChildId && <TomorrowPreviewCard key={surpriseChildId} childId={surpriseChildId} />}

            {/* 7. Reminder nudge — habit formation trigger */}
            <ReminderSuggestionCard />

            {/* 8. Monthly parent letter — emotional reward, reflection */}
            <ParentLetterCard children={children} />

            {/* 9. Referral — word-of-mouth growth loop, shown to paid users */}
            {isPaid && <ReferralCard />}

            {/* 10. Upgrade nudge — one clear CTA, only for free users */}
            {!isPaid && (
              <div className="card border-0 shadow-sm" style={{ borderRadius:'18px', background:'linear-gradient(135deg,#2d1b69,#5a2d9e)', overflow:'hidden' }}>
                <div className="card-body p-4">
                  <div style={{ fontSize:'28px', marginBottom:'8px' }}>✨</div>
                  <h6 className="fw-bold mb-2 text-white" style={{ fontFamily:"'Fredoka One',cursive" }}>Unlock the full experience</h6>
                  <div className="d-flex flex-column gap-2 mb-3">
                    {[
                      ['🎙️', 'Smart narration voice'],
                      ['🎨', 'Regenerate illustrations'],
                      ['📄', 'Export stories as PDF'],
                      ['🌍', '10+ languages'],
                    ].map(([icon, text]) => (
                      <div key={text} style={{ fontSize:'13px', color:'#c9a8ff' }}>
                        <span className="me-2">{icon}</span>{text}
                      </div>
                    ))}
                  </div>
                  <Link to="/pricing" className="btn btn-warning rounded-pill w-100 fw-bold" style={{ fontSize:'13px' }}>
                    See Plans from {pricing.formatted.basic}/month
                  </Link>
                </div>
              </div>
            )}

          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            ZONE 8 — CREDITS & USAGE
            Placed after the main content feed: users who scroll this far
            are engaged and actively checking their quota.
        ══════════════════════════════════════════════════════════════════ */}
        {(() => {
          const creditRows = [
            { key:'stories',           label:'Stories',              icon:'bi-book-fill',          color:'#6f42c1', bg:'#f0e8ff', track:'#e8d5ff', packType:'stories' },
            ...(isPaid ? [{ key:'narrations',     label:'Smart Narrations',     icon:'bi-stars',              color:'#0d6efd', bg:'#e8f0ff', track:'#d0e4ff', packType:'narrations' }] : []),
            ...(isFamily ? [{ key:'cloneNarrations', label:'Voice Narrations',  icon:'bi-mic-fill',           color:'#ea580c', bg:'#fff3ed', track:'#fed7aa', packType:'cloneNarrations' }] : []),
            ...(isPaid ? [{ key:'regens',         label:'Illustration Regens',  icon:'bi-image-fill',         color:'#059669', bg:'#f0fdf4', track:'#bbf7d0', packType:'regens' }] : []),
            { key:'photoIllustrations', label:'Photo Illustrations',  icon:'bi-person-bounding-box', color:'#db2777', bg:'#fdf2f8', track:'#fbcfe8', packType:'photoIllustrations' },
          ];
          return (
            <div data-tour="usage-card" className="card border-0 shadow-sm mt-4 mb-4" style={{ borderRadius:'18px', overflow:'hidden' }}>
              <div className="d-flex align-items-center justify-content-between px-4 py-3"
                style={{ background:'var(--header-bg)', borderBottom:'1px solid var(--border-purple)' }}>
                <h6 className="fw-bold mb-0" style={{ color:'var(--text-main)' }}>
                  <i className="bi bi-lightning-fill me-2" style={{ color:'#6f42c1' }}></i>Credits & Usage
                </h6>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge rounded-pill px-3 py-2" style={{ background: pm.color, color:'#fff', fontSize:'11px' }}>
                    {pm.label} Plan
                  </span>
                  {plan === 'free' && (
                    <Link to="/pricing" className="btn btn-sm btn-dream rounded-pill px-3" style={{ fontSize:'12px' }}>
                      ⭐ Upgrade
                    </Link>
                  )}
                </div>
              </div>
              <div className="px-4 py-3 d-flex flex-column gap-3">
                {limitsLoading ? (
                  <div className="text-center py-2">
                    <span className="spinner-border spinner-border-sm" style={{ color:'#6f42c1' }}></span>
                  </div>
                ) : creditRows.map(row => {
                  const monthlyLimit = limits[row.key] || 0;
                  const rowUsed      = usage[row.key]  || 0;
                  const topUp        = topUpCredits[row.key] || 0;
                  const monthlyLeft  = Math.max(0, monthlyLimit - rowUsed);
                  const totalLeft    = monthlyLeft + topUp;
                  const totalLimit   = monthlyLimit + topUp;
                  const pct          = totalLimit > 0 ? Math.min(100, (rowUsed / totalLimit) * 100) : 0;
                  const barColor     = pct >= 90 ? '#dc3545' : pct >= 70 ? '#fd7e14' : row.color;
                  return (
                    <div key={row.key}>
                      <div className="d-flex align-items-center justify-content-between mb-1">
                        <div className="d-flex align-items-center gap-2">
                          <div className="rounded-2 d-flex align-items-center justify-content-center"
                            style={{ width:'28px', height:'28px', background: row.bg, flexShrink:0 }}>
                            <i className={`bi ${row.icon}`} style={{ color: row.color, fontSize:'13px' }}></i>
                          </div>
                          <span className="fw-semibold" style={{ color:'var(--text-main)', fontSize:'13px' }}>{row.label}</span>
                          {topUp > 0 && (
                            <span className="badge rounded-pill px-2"
                              style={{ background:'var(--badge-yellow-bg)', color:'var(--badge-yellow-fg)', fontSize:'10px', fontWeight:600 }}>
                              +{topUp} top-up
                            </span>
                          )}
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <span style={{ color: totalLeft === 0 ? '#dc3545' : '#555', fontSize:'12px', fontWeight:600 }}>
                            {totalLeft} <span style={{ color:'#aaa', fontWeight:400 }}>/ {totalLimit} left</span>
                          </span>
                          <button className="btn btn-sm rounded-pill fw-semibold px-2 py-0"
                            style={{ background:'#f0e8ff', color:'#6f42c1', border:'none', fontSize:'11px', lineHeight:'22px' }}
                            onClick={() => { setTopUpType(row.packType); setShowTopUp(true); trackTopUpOpened(row.packType); }}>
                            ⚡ Buy More
                          </button>
                        </div>
                      </div>
                      <div style={{ height:'6px', borderRadius:'3px', background: row.track, overflow:'hidden' }}>
                        <div style={{ height:'100%', borderRadius:'3px', width:`${pct}%`, background: barColor, transition:'width 0.6s ease' }} />
                      </div>
                      <div className="d-flex justify-content-between mt-1">
                        <span style={{ color:'#aaa', fontSize:'11px' }}>{rowUsed} used this month</span>
                        {topUp > 0 && (
                          <span style={{ color:'#856404', fontSize:'11px' }}>{monthlyLeft} plan + {topUp} bonus</span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {plan === 'free' && (
                  <div className="rounded-3 p-3 mt-1 d-flex align-items-center justify-content-between gap-3"
                    style={{ background:'var(--purple-section)', border:'1px solid var(--border-purple)' }}>
                    <div>
                      <div className="fw-semibold" style={{ color:'var(--text-main)', fontSize:'13px' }}>Want more stories?</div>
                      <div style={{ color:'#6f42c1', fontSize:'12px' }}>Upgrade for 60+ stories, smart narration & more</div>
                    </div>
                    <Link to="/pricing" className="btn btn-dream btn-sm rounded-pill px-3 fw-semibold flex-shrink-0">
                      See Plans
                    </Link>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* ══════════════════════════════════════════════════════════════════
            ZONE 9 — GROWTH ANALYTICS
            For engaged, data-curious parents. Naturally at the bottom —
            high-value but low-urgency. Self-hides when no data available.
        ══════════════════════════════════════════════════════════════════ */}
        <GrowthDashboard />

      </div>

      <TopUpModal
        show={showTopUp}
        onClose={() => { setShowTopUp(false); setTopUpType(null); refreshLimits(); }}
        packType={topUpType}
        planName={plan}
      />

      {showTour && (
        <ProductTour
          onDone={doneTour}
          onOpenSidebar={() => setMobileSidebarOpen(true)}
          onCloseSidebar={() => setMobileSidebarOpen(false)}
        />
      )}
    </DashboardLayout>
  );
};

export default DashboardPage;
